import FuseUtils from '@fuse/utils/FuseUtils';
import { defaultUserCover } from 'app/configs/themesConfig';

/* Local test mode JWT service - bypasses Cognito */

class JwtServiceLocal extends FuseUtils.EventEmitter {
  constructor() {
    super();
    this.cachedUser = null;
  }

  init() {
    this.handleAuthentication();
  }

  handleAuthentication = async () => {
    // Check if test explicitly disabled auth
    const skipAuth = localStorage.getItem('testSkipAuth') === 'true';
    
    if (skipAuth) {
      console.log("Local auth mode - test disabled auth, skipping auto login");
      this.emit('onNoAccessToken');
      return;
    }
    
    console.log("Local auth mode - auto login");
    this.emit('onAutoLogin', true);
  };

  signInWithToken = () => {
    return new Promise((resolve, reject) => {
      (async () => {
        try {
          // Check if test explicitly disabled auth
          const skipAuth = localStorage.getItem('testSkipAuth') === 'true';
          
          if (skipAuth) {
            console.log('Test disabled auth - no user authentication required');
            reject(new Error('No authentication required'));
            return;
          }
          
          const user = await this.getStoreUserData();
          resolve(user);
        } catch (err) {
          console.log(err);
          reject(err);
        }
      })()
    });
  };

  getStoreUserData = async () => {
    // Return cached user if available
    if (this.cachedUser) {
      console.log('Returning cached user data');
      return this.cachedUser;
    }

    // Otherwise fetch and cache
    this.cachedUser = await this.getLocalMockUser();
    return this.cachedUser;
  };

  getLocalMockUser = async () => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    
    // Get worker ID from localStorage (set by test fixture via addInitScript)
    const workerId = localStorage.getItem('testWorkerId') || 'default';
    
    // Get the default test user ID and JWT from the backend
    let userId = localStorage.getItem('localTestUserId');
    let authToken = localStorage.getItem('localTestAuthToken');
    
    if (!userId || !authToken) {
      try {
        const userResponse = await fetch(apiUrl + '/__test__/user', {
          headers: {
            'X-Worker-Id': workerId
          }
        });
        const userData = await userResponse.json();
        userId = userData.userId;
        authToken = userData.jwt;
        localStorage.setItem('localTestUserId', userId);
        localStorage.setItem('localTestAuthToken', authToken);
        console.log('Fetched test user for worker', workerId, ':', userId);
      } catch (err) {
        console.error('Failed to fetch default user:', err);
        throw err;
      }
    }
    
    // Use the real JWT from backend
    const response = await fetch(apiUrl + '/x2/userx/' + userId + '/validatelogin', {
      method: 'POST',
      headers: {
        'Authorization': authToken,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      console.error('validateLogin failed with status:', response.status);
      throw new Error(`Authentication failed: ${response.status}`);
    }
    
    const data = await response.json();
    const userObj = data.modelA || data.user || data;
    
    return {
      id: userId,
      timelineId: userObj.publicTimelineId,
      role: [userObj.type || 'provider'],
      data: {
        screenName: userObj.screenName || 'testuser',
        firstName: userObj.firstName || 'Test',
        lastName: userObj.lastName || 'User',
        email: userObj.email || 'test@example.com',
        type: userObj.type || 'provider',
        status: userObj.status || '',
        providerInfoId: userId
      },
      images: {
        profileImageUrl: userObj.profileImageUrl,
        photoURL: userObj.profileImageUrl,
        headerImageURL: userObj.headerImageUrl || defaultUserCover
      },
      sessionSettings: { welcomeMessage: true, emergencyDisclaimer: true },
      providerInfo: userObj.providerInfo,
      requireAcceptTerms: false,
      raw: userObj,
      initialized: false,
      subscriptionStatus: {},
      identificationStatus: {}
    };
  };

  getAccessToken = () => {
    return localStorage.getItem('localTestAuthToken');
  };

  refreshLogin = () => {
    return Promise.resolve();
  };

  logout = () => {
    this.cachedUser = null;
    localStorage.removeItem('localTestUserId');
    localStorage.removeItem('localTestAuthToken');
    this.emit('onLogout', 'Logged out');
  };

  // Stub methods for compatibility
  createUser = () => Promise.reject('Not supported in local mode');
  signInWithEmailAndPassword = () => Promise.reject('Not supported in local mode');
  signInWithMfaCode = () => Promise.reject('Not supported in local mode');
  confirmSignupWithCode = () => Promise.reject('Not supported in local mode');
  resendConfirmationCode = () => Promise.reject('Not supported in local mode');
  forgotPassword = () => Promise.reject('Not supported in local mode');
  forgotPasswordReset = () => Promise.reject('Not supported in local mode');
  changePassword = () => Promise.reject('Not supported in local mode');
  newPassword = () => Promise.reject('Not supported in local mode');
}

const instance = new JwtServiceLocal();

export default instance;
